import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, Zap } from 'lucide-react';

const Navbar = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);

    useEffect(() => {
        const handleScroll = () => setIsScrolled(window.scrollY > 10);
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        document.body.style.overflow = isOpen ? 'hidden' : '';
        return () => { document.body.style.overflow = ''; };
    }, [isOpen]);

    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth >= 768 && isOpen) setIsOpen(false);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [isOpen]);

    const scrollToSection = useCallback((id) => {
        setIsOpen(false);
        setTimeout(() => {
            const element = document.getElementById(id);
            if (element) {
                const offset = 80;
                const bodyRect = document.body.getBoundingClientRect().top;
                const elementRect = element.getBoundingClientRect().top;
                const elementPosition = elementRect - bodyRect;
                window.scrollTo({ top: elementPosition - offset, behavior: 'smooth' });
            }
        }, 100);
    }, []);

    const navLinks = [
        { label: 'Features', id: 'features' },
        { label: 'Vision', id: 'vision' },
        { label: 'For Teams', id: 'teams' },
    ];

    return (
        <>
            <nav className={`fixed top-0 left-0 z-50 w-full transition-all duration-500 ${isScrolled ? 'bg-[#0a0a0a]/80 backdrop-blur-xl border-b border-cyan-500/10 shadow-[0_1px_20px_rgba(0,240,255,0.05)]' : 'bg-transparent border-b border-transparent'}`}>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between h-16 sm:h-20 items-center">
                        <button
                            onClick={() => { setIsOpen(false); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                            className="flex-shrink-0 flex items-center gap-2.5 hover:opacity-80 transition-opacity z-50 group"
                        >
                            <div className="w-9 h-9 sm:w-10 sm:h-10 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg flex items-center justify-center text-white shadow-lg shadow-cyan-500/20 group-hover:shadow-cyan-500/40 transition-shadow">
                                <Zap className="w-5 h-5 sm:w-6 sm:h-6 fill-current" />
                            </div>
                            <span className="font-bold text-lg sm:text-xl tracking-tight text-white">Imago</span>
                        </button>

                        <div className="hidden md:flex items-center gap-1">
                            {navLinks.map((link) => (
                                <button
                                    key={link.id}
                                    onClick={() => scrollToSection(link.id)}
                                    className="relative px-4 py-2 text-sm font-medium text-gray-400 hover:text-cyan-400 transition-colors group"
                                >
                                    {link.label}
                                    <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-[2px] bg-gradient-to-r from-cyan-500 to-blue-500 group-hover:w-3/4 transition-all duration-300 rounded-full shadow-[0_0_8px_rgba(0,240,255,0.5)]" />
                                </button>
                            ))}
                        </div>

                        <div className="hidden md:flex items-center gap-4">
                            <Link to="/sign-in-sso" className="text-gray-400 hover:text-white font-mono text-sm transition-colors">
                                Sign In
                            </Link>
                            <Link to="/sign-up" className="relative group px-6 py-2.5 rounded-lg font-mono font-bold text-sm text-black bg-gradient-to-r from-cyan-400 to-cyan-300 hover:from-cyan-300 hover:to-cyan-200 transition-all shadow-lg shadow-cyan-500/20 hover:shadow-cyan-500/40">
                                <span className="relative z-10">Sign Up</span>
                            </Link>
                        </div>

                        <button
                            onClick={() => setIsOpen(!isOpen)}
                            className="md:hidden text-gray-400 hover:text-cyan-400 focus:outline-none p-2 -mr-2 z-50 relative transition-colors"
                            aria-label={isOpen ? 'Close menu' : 'Open menu'}
                            aria-expanded={isOpen}
                        >
                            <div className="relative w-6 h-6">
                                <X className={`h-6 w-6 absolute inset-0 transition-all duration-200 ${isOpen ? 'opacity-100 rotate-0' : 'opacity-0 rotate-90'}`} />
                                <Menu className={`h-6 w-6 absolute inset-0 transition-all duration-200 ${isOpen ? 'opacity-0 -rotate-90' : 'opacity-100 rotate-0'}`} />
                            </div>
                        </button>
                    </div>
                </div>
            </nav>

            {isOpen && (
                <div className="md:hidden fixed inset-0 bg-black/60 backdrop-blur-sm z-40" onClick={() => setIsOpen(false)} aria-hidden="true" />
            )}

            <div
                className={`md:hidden fixed top-16 sm:top-20 left-0 right-0 z-40 bg-[#0a0a0a]/95 backdrop-blur-xl border-b border-cyan-500/10 shadow-[0_10px_40px_rgba(0,240,255,0.05)] transition-all duration-300 ease-out origin-top ${
                    isOpen ? 'opacity-100 scale-y-100 translate-y-0' : 'opacity-0 scale-y-95 -translate-y-2 pointer-events-none'
                }`}
                style={{ transformOrigin: 'top center' }}
            >
                <div className="px-4 py-4 space-y-1 max-h-[calc(100vh-4rem)] overflow-y-auto">
                    {navLinks.map((link) => (
                        <button
                            key={link.id}
                            onClick={() => scrollToSection(link.id)}
                            className="block w-full text-left px-4 py-3.5 rounded-lg text-sm font-mono text-gray-300 hover:text-cyan-400 hover:bg-cyan-500/5 active:bg-cyan-500/10 transition-colors"
                        >
                            <span className="text-cyan-500/50 mr-2">&gt;</span>
                            {link.label}
                        </button>
                    ))}
                    <div className="border-t border-white/5 my-3"></div>
                    <Link to="/sign-in-sso" onClick={() => setIsOpen(false)} className="block px-4 py-3.5 rounded-lg text-sm font-mono text-gray-300 hover:text-white hover:bg-white/5 transition-colors">
                        <span className="text-cyan-500/50 mr-2">&gt;</span>
                        Sign In
                    </Link>
                    <div className="pt-2 pb-1">
                        <Link to="/sign-up" onClick={() => setIsOpen(false)} className="block w-full px-4 py-3.5 rounded-lg text-sm font-mono font-bold text-center bg-gradient-to-r from-cyan-500 to-blue-500 text-black hover:from-cyan-400 hover:to-blue-400 transition-all shadow-lg shadow-cyan-500/20">
                            Sign Up
                        </Link>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Navbar;
